/**
 * Auto-adds / removes the configured free gift product when cart
 * spend crosses the threshold (default £34).
 */
(() => {
  const PROPERTY_KEY = '_free_gift';
  let syncing = false;
  let queuedCart = null;
  let config = null;

  const readConfig = () => {
    const nodes = document.querySelectorAll('#oozz-free-gift-config, [data-oozz-free-gift-config]');
    for (const node of nodes) {
      try {
        const parsed = JSON.parse(node.textContent);
        if (parsed?.enabled && parsed?.variantId && parsed?.thresholdCents) {
          return parsed;
        }
      } catch (error) {
        // ignore invalid nodes
      }
    }
    return theme?.settings?.freeGift || null;
  };

  const getVariantId = (item) => item.variant_id || item.id;

  const isGiftItem = (item) => {
    if (!config) return false;
    if (getVariantId(item) === config.variantId) return true;
    return !!(item.properties && item.properties[PROPERTY_KEY]);
  };

  const isExcludedItem = (item) => {
    if (isGiftItem(item)) return true;
    const id = getVariantId(item);
    return !!(config?.giftWrapVariantId && id === config.giftWrapVariantId);
  };

  const eligibleTotal = (cart) => {
    return (cart.items || []).reduce((total, item) => {
      if (isExcludedItem(item)) return total;
      return total + (item.final_line_price || 0);
    }, 0);
  };

  const giftQuantity = (cart) => {
    return (cart.items || []).reduce((qty, item) => {
      return isGiftItem(item) ? qty + item.quantity : qty;
    }, 0);
  };

  const paidItemCount = (cart) => {
    return (cart.items || []).reduce((count, item) => {
      return isExcludedItem(item) ? count : count + item.quantity;
    }, 0);
  };

  const bundleSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  const publishCart = (cart) => {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source: 'free-gift',
      cart
    });
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart }
      })
    );
  };

  const fetchCart = async () => {
    return (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
  };

  const addGift = async () => {
    const sectionsToBundle = bundleSections();
    const quantity = config.quantity || 2;
    const formData = new FormData();
    formData.append('id', String(config.variantId));
    formData.append('quantity', String(quantity));
    formData.append(`properties[${PROPERTY_KEY}]`, '1');
    if (sectionsToBundle.length) {
      formData.append('sections', sectionsToBundle.join(','));
      formData.append('sections_url', theme.utils.sectionsUrl());
    }

    const requestConfig = theme.utils.fetchConfig('javascript');
    requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete requestConfig.headers['Content-Type'];
    requestConfig.body = formData;

    const parsedState = await (await fetch(theme.routes.cart_add_url, requestConfig)).json();
    if (parsedState.status) {
      console.warn('[free-gift] Unable to add gift:', parsedState.description || parsedState.message);
      return null;
    }

    const cartJson = await fetchCart();
    cartJson.sections = parsedState.sections;
    return cartJson;
  };

  const setGiftQuantity = async (quantity) => {
    const sectionsToBundle = bundleSections();
    const body = JSON.stringify({
      updates: {
        [config.variantId]: quantity
      },
      ...theme.utils.cartSectionsParams(sectionsToBundle)
    });

    return (await fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), body })).json();
  };

  const sync = async (cart) => {
    config = readConfig();
    if (!config?.enabled || !config?.variantId || !config?.thresholdCents) return;

    const targetQty = config.quantity || 2;

    if (syncing) {
      queuedCart = cart || true;
      return;
    }

    syncing = true;

    try {
      let currentCart = cart;
      if (!currentCart || typeof currentCart.items === 'undefined') {
        currentCart = await fetchCart();
      }

      const shouldHaveGift =
        paidItemCount(currentCart) > 0 && eligibleTotal(currentCart) >= config.thresholdCents;
      const currentGiftQty = giftQuantity(currentCart);

      if (shouldHaveGift && currentGiftQty === targetQty) return;
      if (!shouldHaveGift && currentGiftQty === 0) return;

      let nextCart = null;

      if (shouldHaveGift && currentGiftQty === 0) {
        nextCart = await addGift();
      } else if (shouldHaveGift && currentGiftQty !== targetQty) {
        nextCart = await setGiftQuantity(targetQty);
      } else {
        nextCart = await setGiftQuantity(0);
      }

      if (nextCart && !nextCart.status) {
        publishCart(nextCart);
      }
    } catch (error) {
      console.error('[free-gift] sync failed', error);
    } finally {
      syncing = false;

      if (queuedCart !== null) {
        const next = queuedCart === true ? null : queuedCart;
        queuedCart = null;
        sync(next);
      }
    }
  };

  const onCartUpdate = (event) => {
    if (event?.source === 'free-gift') return;
    if (event?.cart?.errors) return;
    sync(event?.cart);
  };

  const start = () => {
    config = readConfig();
    if (!config?.enabled || !config?.variantId || !config?.thresholdCents) return;

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    document.addEventListener('cart:updated', (event) => {
      if (event?.detail?.cart) sync(event.detail.cart);
    });
    sync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
